import { useState } from 'react';
import {
  Box, Typography, Stack, TextField, InputAdornment, Chip, Button, MenuItem,
  Collapse, alpha, useTheme,
} from '@mui/material';
import { Search, Add, FilterList, ClearAll, FileDownload } from '@mui/icons-material';
import { USER_STATUS_OPTIONS, USER_TYPE_OPTIONS } from '../../constants/userManagement';

interface UserOpsBarProps {
  search: string;
  onSearchChange: (q: string) => void;
  typeFilter: string;
  onTypeFilterChange: (type: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  totalCount: number;
  filteredCount: number;
  onAdd: () => void;
  onClearFilters: () => void;
  title?: string;
  onExport?: () => void;
  exporting?: boolean;
}

export default function UserOpsBar({
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  totalCount,
  filteredCount,
  onAdd,
  onClearFilters,
  title = 'All Users',
  onExport,
  exporting,
}: UserOpsBarProps) {
  const theme = useTheme();
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2.5,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.03) }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {filteredCount === totalCount ? `${totalCount} users` : `${filteredCount} of ${totalCount} users`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {onExport && (
            <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={onExport} disabled={exporting || filteredCount === 0}>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export</Box>
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<FilterList />} onClick={() => setFiltersOpen((v) => !v)}>
            Filters
          </Button>
          <Button size="small" variant="contained" startIcon={<Add />} onClick={onAdd} sx={{ flex: { xs: 1, sm: '0 0 auto' } }}>
            Add User
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
        {USER_STATUS_OPTIONS.map((opt) => (
          <Chip
            key={opt.value || 'all'}
            label={opt.label}
            size="small"
            onClick={() => onStatusFilterChange(opt.value)}
            color={statusFilter === opt.value ? opt.color : 'default'}
            variant={statusFilter === opt.value ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          />
        ))}
      </Box>

      <Box sx={{ px: 2, pb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search name, email, or phone…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Collapse in={filtersOpen}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ px: 2, pb: 2, pt: 0, flexWrap: 'wrap', alignItems: { sm: 'center' } }}
        >
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter || 'all'}
            onChange={(e) => onTypeFilterChange(e.target.value === 'all' ? '' : e.target.value)}
            sx={{ minWidth: { sm: 160 }, width: { xs: '100%', sm: 'auto' } }}
          >
            {USER_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value || 'all'}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <Button size="small" variant="text" startIcon={<ClearAll />} onClick={onClearFilters}>
            Clear filters
          </Button>
        </Stack>
      </Collapse>
    </Box>
  );
}
